/**
 * WHEN THE NEXT CHECK-IN COMES, IN WORDS SHE CANNOT PLAN AROUND.
 *
 * AD-11 §2.4, under the operator's ruling (CFG-41, decision #131): the app does
 * not announce its own sampling schedule. A schedule she can read is one she can
 * arrange her day around, and a sampling method that announces itself stops
 * sampling representative moments.
 *
 * ONE IMPLEMENTATION, TWO SURFACES. Home and the beep-completion screen both
 * call this, because AD-11 §2.2's requirement is that they "must never disagree
 * about when the next one is, which they can today". Two copies of an estimate
 * is exactly how they would.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * FIVE RULES THE WORDING OBEYS. None of them is decoration.
 *
 *  1. NEVER A CLOCK TIME. Both `toLocaleTimeString` calls that used to live in
 *     Home.jsx are deleted, not adjusted.
 *  2. THE HEDGE DOES THE WORK. "about", "around", "to". These are what make it
 *     an estimate rather than an appointment. Do not edit them out for brevity.
 *  3. NO MINUTES, EVER. The floor is "in around an hour". Minutes are
 *     plannable; hours are not. That is why the table bottoms out where it does.
 *  4. NO COUNTDOWN AND NO REFINEMENT. Computed once per render from a `now` the
 *     CALLER captures, never from a timer inside here. A number that counts
 *     down is a countdown, and a countdown is an appointment.
 *  5. WHEN THE APP DOES NOT KNOW, IT SAYS NOTHING. Every unknown returns null
 *     and the caller renders the lead line alone. Silence is honest; a promise
 *     is not.
 *
 * Rule 5 is why `"We'll send you a reminder when the next one is ready."` does
 * not appear in this file or anywhere else. It was DELETED rather than reworded
 * (AD-11 §2.4): `next_beep_at: null` used to mean both "not scheduled yet" and
 * "the study is over", and that string assumed the first. It is the sentence
 * that lied to a finished participant.
 * ═══════════════════════════════════════════════════════════════════════
 *
 * THE STRINGS ARE CONFIG'S (AD-11 §2.5, decision #92). They are not landed in
 * `screens.json` yet, so each reads `copy?.<key> ?? <literal>` — the same shape
 * `Notifications.jsx` uses for `install.email_route`. When config lands
 * `screens.waiting` this file changes by zero lines.
 */

/**
 * DEFAULTS ARE THE FALLBACK, NOT THE SOURCE. Verbatim from AD-11 §2.4, with the
 * em dash rule (CFG-41) applied: plain spaced hyphens, no em dashes anywhere a
 * participant reads.
 */
export const WAITING_COPY = {
  lead: 'Nothing to do right now.',
  estimate_tomorrow: 'The next one comes tomorrow.',
  estimate_hour: 'The next one is in around an hour.',
  // {n} / {a} / {b} are filled from the clock, never from config — a config file
  // never holds a number (screens.json's own note).
  estimate_hours_template: 'The next one is in about {n} hours.',
  estimate_range_template: 'The next one is in {a} to {b} hours.',

  // (b) the last check-in of the day — slot B, the bed beep. It names a DAY and
  // never a time; a day is not plannable at a resolution that damages sampling.
  last_of_day_title: "That's it for today",
  last_of_day_body: 'Nothing more tonight - the next one comes tomorrow.',

  // Every other check-in. The title carries the completion and the body carries
  // the waiting line, identical to home's.
  beep_done_title: "That's this one done",

  // After day 0, before the first check-in has ever arrived. "later today" and
  // not "later tonight": day 0 can finish at any hour, and a woman finishing at
  // 09:00 told "later tonight" about a 14:00 beep has been told something false.
  first_today: 'The first one comes later today.',
  first_tomorrow: 'The first one comes tomorrow.',
}

/**
 * One string, config's if config has it and the AD-11 literal until it does.
 *
 * THE ONLY ACCESSOR. Every string on both surfaces comes through here, so
 * config landing `screens.waiting` needs no edit at any call site.
 */
export const waitingText = (copy, key) => copy?.[key] ?? WAITING_COPY[key]
const line = waitingText

/** Local midnight of an instant. Local, because "tomorrow" is a calendar word. */
function startOfLocalDay(ms) {
  const d = new Date(ms)
  d.setHours(0, 0, 0, 0)
  return d.getTime()
}

/**
 * Whole calendar days from `now` to `then`, in LOCAL time.
 *
 * Rounded rather than floored so a DST boundary — a 23- or 25-hour day — cannot
 * turn "tomorrow" into "today" or into two days.
 */
function calendarDaysAhead(nowMs, thenMs) {
  return Math.round((startOfLocalDay(thenMs) - startOfLocalDay(nowMs)) / 86_400_000)
}

/** null unless the server named a beep and the string parses. */
function instantOf(iso) {
  if (!iso) return null
  const ms = Date.parse(iso)
  return Number.isFinite(ms) ? ms : null
}

/**
 * The estimate sentence, or null for silence.
 *
 * THE SELECTOR ORDER IS THE RULE (AD-11 §2.4). Study-complete and the open beep
 * are the caller's to test FIRST — they are not estimates and this function is
 * never reached in either state.
 *
 *   3  next_beep_at is NULL ......... null (lead line only, no promise)
 *   5  a later calendar day ......... "The next one comes tomorrow."
 *   6  h < 1.25 .................... "The next one is in around an hour."
 *   7  |h - round(h)| <= 0.25 ....... "The next one is in about {n} hours."
 *   8  otherwise .................... "The next one is in {floor} to {ceil} hours."
 *
 * @param {object|null} copy   `screensOf(bundle)?.waiting`, when config lands it
 * @param {string|null} nextBeepAt  ISO instant from GET /api/beeps/current
 * @param {number} now         ms, captured ONCE by the caller. See rule 4.
 * @returns {string|null}
 */
export function estimateLine(copy, nextBeepAt, now) {
  const at = instantOf(nextBeepAt)
  if (at === null) return null

  const days = calendarDaysAhead(now, at)
  if (days === 1) return line(copy, 'estimate_tomorrow')
  /*
   * MORE THAN ONE DAY OUT HAS NO APPROVED WORD, so it gets none.
   *
   * Unreachable on today's schedule — the longest real gap is one night, the
   * bed beep to the next wake beep — but "tomorrow" would be plainly false if
   * it ever fired, and rule 5 already says what to do when the app does not
   * have a word: say nothing.
   */
  if (days > 1) return null

  const h = (at - now) / 3_600_000
  /*
   * ALREADY DUE, OR PAST. The scheduler ticks every 30s, so there is a real
   * window where a beep's time has arrived and it has not been notified yet.
   * "in around an hour" about a beep that is already due is the same class of
   * false promise this whole file exists to remove.
   */
  if (h <= 0) return null

  if (h < 1.25) return line(copy, 'estimate_hour')

  const n = Math.round(h)
  /*
   * n >= 2 IS STRUCTURAL, NOT INCIDENTAL. AD-11 states "in about 1 hours" is
   * unreachable because rule 6 catches anything rounding to 1 — true for every
   * h except exactly 1.25, where Math.round gives 1. Guarding here makes the
   * claim true by construction instead of by luck, and that h falls through to
   * the range, which reads correctly ("in 1 to 2 hours").
   */
  if (n >= 2 && Math.abs(h - n) <= 0.25) {
    return line(copy, 'estimate_hours_template').replace('{n}', String(n))
  }
  return line(copy, 'estimate_range_template')
    .replace('{a}', String(Math.floor(h)))
    .replace('{b}', String(Math.ceil(h)))
}

/**
 * The whole waiting line: the lead, plus the estimate when there is one.
 *
 * Identical on home and on the beep-completion screen, which is the point.
 */
export function waitingLine(copy, nextBeepAt, now) {
  const estimate = estimateLine(copy, nextBeepAt, now)
  const lead = line(copy, 'lead')
  return estimate ? `${lead} ${estimate}` : lead
}

/**
 * After day 0, before the first check-in has ever arrived.
 *
 * A relative word derived from `next_beep_at`'s local calendar date — which
 * also fixes a claim that was false, not merely over-disclosing: the old
 * "arrives tomorrow" branch is wrong for a day 0 finishing at 23:00, where the
 * 12-hour floor lands after the next day's wake beep and the first check-in is
 * the morning AFTER tomorrow.
 *
 * @returns {string|null} null when the app does not know. Rule 5.
 */
export function firstCheckInLine(copy, nextBeepAt, now) {
  const at = instantOf(nextBeepAt)
  if (at === null) return null
  const days = calendarDaysAhead(now, at)
  if (days === 1) return line(copy, 'first_tomorrow')
  if (days !== 0) return null
  return line(copy, 'first_today')
}

/**
 * The bed beep, last of its day.
 *
 * `slot` comes from the beep payload (`W R1 R2 R3 R4 B`) and is never inferred
 * from a count or a clock. Backend deliberately added no `last_of_day` field:
 * the slot is already there, and a second field stating the same thing is a
 * second thing that can disagree.
 */
export const isLastOfDay = (slot) => slot === 'B'
